import {
  reportingPrisma as prisma,
  OrderItemStatus,
  OrderStatus,
  PaymentStatus,
} from "client";
import { getEventTicketOids } from "./getEventTicketOids";

/**
 * Daily ticket revenue — sums paid order items (tickets).
 * Only confirmed tickets from completed orders with PAID payments.
 */
export const getProductsRevenueMetrics = async ({
  eventId,
  filters,
  ticketOids: ticketOidsArg,
}: {
  eventId: string;
  filters: { [key: string]: string };
  /** Prefetch via getEventTicketOids to avoid a second Ticket lookup. */
  ticketOids?: Array<{ $oid: string }>;
}) => {
  try {
    const ticketOids = ticketOidsArg ?? (await getEventTicketOids(eventId));
    if (ticketOids.length === 0) return [];

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const dateMatchStages: object[] = [];
    if (filters.fromDate && filters.toDate) {
      const fromIso = new Date(filters.fromDate).toISOString();
      const toIso = new Date(filters.toDate).toISOString();
      dateMatchStages.push({
        $match: {
          $expr: {
            $and: [
              {
                $gte: [{ $toDate: "$order.createdAt" }, { $toDate: fromIso }],
              },
              {
                $lte: [{ $toDate: "$order.createdAt" }, { $toDate: toIso }],
              },
            ],
          },
        },
      });
    }

    const stages = [
      {
        $match: {
          status: OrderItemStatus.CONFIRMED,
          ticketId: { $in: ticketOids },
        },
      },
      {
        $lookup: {
          from: "Ticket",
          localField: "ticketId",
          foreignField: "_id",
          as: "ticket",
        },
      },
      { $unwind: "$ticket" },
      {
        // Re-check soft-delete at aggregate time (ids may have been deleted after prefetch).
        $match: {
          $or: [
            { "ticket.deletedAt": null },
            { "ticket.deletedAt": { $exists: false } },
          ],
        },
      },
      {
        $lookup: {
          from: "BookableItem",
          localField: "ticket.bookableItemId",
          foreignField: "_id",
          as: "bookableItem",
        },
      },
      { $unwind: { path: "$bookableItem", preserveNullAndEmptyArrays: true } },
      {
        $lookup: {
          from: "Order",
          localField: "orderId",
          foreignField: "_id",
          as: "order",
        },
      },
      { $unwind: "$order" },
      {
        $match: {
          "order.status": OrderStatus.COMPLETED,
        },
      },
      ...dateMatchStages,
      {
        $lookup: {
          from: "Payment",
          localField: "order._id",
          foreignField: "orderId",
          as: "payments",
        },
      },
      {
        $addFields: {
          today: {
            $dateToString: {
              format: "%Y/%m/%d",
              date: "$order.createdAt",
              timezone: userTimeZone,
            },
          },
          hasPaidPayment: {
            $gt: [
              {
                $size: {
                  $filter: {
                    input: "$payments",
                    as: "p",
                    cond: { $eq: ["$$p.status", PaymentStatus.PAID] },
                  },
                },
              },
              0,
            ],
          },
          orderItemTotal: "$total",
          bookablePrice: { $ifNull: ["$bookableItem.price", 0] },
          revenueAmount: {
            $cond: {
              if: { $ne: ["$total", null] },
              then: "$total",
              else: "$bookablePrice",
            },
          },
          isPaidTicket: {
            $cond: {
              if: { $ne: ["$total", null] },
              then: { $gt: ["$total", 0] },
              else: { $gt: ["$bookablePrice", 0] },
            },
          },
        },
      },
      {
        $group: {
          _id: "$today",
          Revenue: {
            $sum: {
              $cond: {
                if: {
                  $and: [
                    { $eq: ["$hasPaidPayment", true] },
                    { $eq: ["$isPaidTicket", true] },
                  ],
                },
                then: "$revenueAmount",
                else: 0,
              },
            },
          },
        },
      },
      { $sort: { _id: 1 } },
    ];

    const result = await prisma.orderItem.aggregateRaw({
      pipeline: stages,
    });
    return result || [];
  } catch (error) {
    throw error;
  }
};
